<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import { useAuth } from "@/composables/useAuth";
import ArgonButton from "@/components/ArgonButton.vue";

const { user } = useAuth();
const puedePagar = computed(() => user.value?.role === "admin" || user.value?.role === "platform_admin");

const subscription = ref(null);
const invoices = ref([]);
const plans = ref([]);
const cargando = ref(true);
const procesandoPago = ref(null); // guarda el id del plan que se está pagando, para deshabilitar solo ese botón

async function cargar() {
  const [subRes, plansRes] = await Promise.all([
    api.get("/billing/subscription"),
    api.get("/billing/plans")
  ]);

  subscription.value = subRes.data.subscription;
  invoices.value = subRes.data.invoices;
  plans.value = plansRes.data.plans;
}

function cargarScriptWompi() {
  return new Promise((resolve, reject) => {
    if (window.WidgetCheckout) return resolve();

    const script = document.createElement("script");
    script.src = "https://checkout.wompi.co/widget.js";
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

async function elegirPlan(plan) {
  if (plan.contact_sales) {
    Swal.fire({
      icon: "info",
      title: "Hablemos de tu plan Enterprise",
      text: "Escríbenos y armamos el plan a la medida de tu operación."
    });
    return;
  }

  procesandoPago.value = plan.id;

  try {
    const res = await api.post("/billing/checkout", { planId: plan.id });
    const { widget } = res.data;

    if (!widget.configured) {
      Swal.fire({
        icon: "info",
        title: "Los pagos todavía no están configurados",
        text: "Contacta al soporte de la plataforma para activar el cobro con Wompi."
      });
      await cargar();
      return;
    }

    await cargarScriptWompi();

    const checkout = new window.WidgetCheckout({
      currency: widget.currency,
      amountInCents: widget.amountInCents,
      reference: widget.reference,
      publicKey: widget.publicKey,
      signature: widget.signature ? { integrity: widget.signature } : undefined,
      redirectUrl: window.location.href
    });

    checkout.open((result) => {
      cargar();

      if (result?.transaction?.status === "APPROVED") {
        Swal.fire({ icon: "success", title: "¡Pago aprobado!", timer: 2500, showConfirmButton: false });
      } else {
        Swal.fire({ icon: "warning", title: "El pago no se completó", text: "Puedes intentarlo de nuevo cuando quieras." });
      }
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo iniciar el pago",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    procesandoPago.value = null;
  }
}

function formatFecha(fecha) {
  return fecha ? new Date(fecha).toLocaleDateString("es-CO") : "—";
}

function formatCOP(valor) {
  return valor != null ? `$${Number(valor).toLocaleString("es-CO")}` : "—";
}

function estadoLabel(status) {
  return {
    trial: "En periodo de prueba",
    active: "Activa",
    past_due: "Vencida",
    cancelled: "Cancelada"
  }[status] || status || "Sin suscripción";
}

function estadoBadgeClass(status) {
  return {
    trial: "bg-info text-dark",
    active: "bg-success",
    past_due: "bg-danger",
    cancelled: "bg-gradient-secondary"
  }[status] || "bg-secondary";
}

function estadoFacturaLabel(status) {
  return { pending: "Pendiente", paid: "Pagada", failed: "Fallida" }[status] || status;
}

onMounted(async () => {
  cargando.value = true;
  await cargar();
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">
    <h4 class="mb-3 subscription-header-title">
      <i class="bi bi-credit-card me-2"></i>
      Mi plan y facturación
    </h4>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <template v-else>
      <div class="row g-3 mb-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">Estado de la suscripción</h6>
                <span class="badge" :class="estadoBadgeClass(subscription?.status)">
                  {{ estadoLabel(subscription?.status) }}
                </span>
              </div>

              <p v-if="subscription">
                Plan actual: <strong>{{ subscription.plan_name }}</strong> — {{ formatCOP(subscription.price_cop) }} COP/mes
              </p>

              <p v-if="subscription?.status === 'trial'">
                Tu prueba gratuita termina el <strong>{{ formatFecha(subscription.trial_ends_at) }}</strong>.
              </p>

              <p v-else-if="subscription?.status === 'active'">
                Tu periodo actual vence el <strong>{{ formatFecha(subscription.current_period_end) }}</strong>.
              </p>

              <p v-else-if="subscription?.status === 'past_due'" class="text-danger">
                Tu suscripción está vencida. Elige un plan abajo para reactivarla.
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">Historial de facturas</h6>
            </div>

            <div class="card-body">
              <div v-if="invoices.length === 0" class="text-muted">
                Todavía no hay facturas.
              </div>

              <ul v-else class="list-group">
                <li v-for="inv in invoices" :key="inv.id" class="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <div>{{ formatCOP(inv.amount_cop) }} COP</div>
                    <small class="text-muted">{{ formatFecha(inv.createdAt) }}</small>
                  </div>
                  <span
                    class="badge"
                    :class="{ 'bg-success': inv.status === 'paid', 'bg-warning': inv.status === 'pending', 'bg-danger': inv.status === 'failed' }"
                  >
                    {{ estadoFacturaLabel(inv.status) }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- PLANES -->
      <h5 class="mb-3">Planes disponibles</h5>
      <div class="row g-3">
        <div v-for="plan in plans" :key="plan.id" class="col-md-4">
          <div class="card h-100" :class="{ 'border-primary shadow': plan.highlight }">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between align-items-start mb-1">
                <h6 class="mb-0">
                  {{ plan.name }}
                  <span v-if="plan.highlight" class="badge bg-primary ms-1">Recomendado</span>
                </h6>
                <span v-if="subscription?.plan_id === plan.id" class="badge bg-success">Tu plan</span>
              </div>

              <p class="text-muted small">{{ plan.tagline }}</p>

              <div class="mb-3">
                <span v-if="plan.contact_sales" class="h5">Desde {{ formatCOP(plan.price_cop) }} COP</span>
                <span v-else class="h4 fw-bold">{{ formatCOP(plan.price_cop) }} <small class="text-muted fs-6">COP/mes</small></span>
              </div>

              <ul class="list-unstyled small flex-grow-1">
                <li v-for="(f, i) in plan.features" :key="i" class="mb-1">
                  <i class="bi bi-check-circle-fill text-success me-1"></i>{{ f }}
                </li>
              </ul>

              <argon-button
                v-if="puedePagar"
                :color="plan.highlight ? 'primary' : 'secondary'"
                :disabled="procesandoPago === plan.id || subscription?.plan_id === plan.id"
                @click="elegirPlan(plan)"
              >
                <span v-if="subscription?.plan_id === plan.id">Plan actual</span>
                <span v-else-if="plan.contact_sales">Hablar con ventas</span>
                <span v-else>Elegir este plan</span>
              </argon-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.subscription-header-title {
  color: #fff !important;
}
</style>
